import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaCheck, FaPalette, FaPaintBrush } from 'react-icons/fa';
import { API_URL } from '../../config/api';
import './TenantThemeSelector.css';

const TenantThemeSelector = ({ tenantId }) => {
  const [themes, setThemes] = useState([]);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');

  const fetchThemes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/theme/saas/tenants/${tenantId}/frontend-themes`);
      if (response.data.success && response.data.data) {
        const themesList = response.data.data.themes || [];
        setThemes(themesList);
        setSelectedTheme(response.data.data.selectedTheme || null);
      } else {
        setThemes([]);
        setSelectedTheme(null);
      }
    } catch (error) {
      console.error('Error fetching themes:', error);
      toast.error(error.response?.data?.message || 'Error fetching themes');
      setThemes([]);
      setSelectedTheme(null);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchThemes();
  }, [fetchThemes]);

  const handleThemeSelect = async (themeCode) => {
    try {
      setSaving(true);
      await axios.put(`${API_URL}/api/theme/saas/tenants/${tenantId}/frontend-theme`, {
        themeCode
      });
      setSelectedTheme(themeCode);
      toast.success('Theme updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating theme');
      console.error('Error updating theme:', error);
    } finally {
      setSaving(false);
    }
  };

  const themesByCategory = (themes || []).reduce((acc, theme) => {
    if (theme && theme.category) {
      if (!acc[theme.category]) acc[theme.category] = [];
      acc[theme.category].push(theme);
    }
    return acc;
  }, {});

  const filteredCategories = categoryFilter === 'all' 
    ? Object.keys(themesByCategory)
    : (themesByCategory[categoryFilter] ? [categoryFilter] : []);

  if (loading) {
    return <div className="loading">Loading themes...</div>;
  }

  return (
    <div className="tenant-theme-selector">
      <div className="theme-selector-header">
        <div>
          <h2><FaPalette /> User Frontend Theme</h2>
          <p>Select a theme template for the user-facing frontend. The selected theme will determine the UI appearance and styling.</p>
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="category-filter"
        >
          <option value="all">All Categories</option>
          <option value="art_craft">Art & Craft</option>
          <option value="modern">Modern</option>
          <option value="luxury">Luxury</option>
        </select>
      </div>

      {selectedTheme && themes && themes.length > 0 && (
        <div className="current-theme-banner">
          <FaPaintBrush /> Currently Selected: <strong>{themes.find(t => t && t.code === selectedTheme)?.name || selectedTheme}</strong>
        </div>
      )}

      {filteredCategories.length === 0 ? (
        <div className="no-themes">No themes found</div>
      ) : (
        filteredCategories.map(category => {
          const categoryThemes = themesByCategory[category] || [];
          return (
          <div key={category} className="theme-category">
            <h3 className="category-title">{category.replace('_', ' ').toUpperCase()}</h3>
            <div className="themes-grid">
              {categoryThemes.map(theme => (
                <div
                  key={theme.code}
                  className={`theme-card ${selectedTheme === theme.code ? 'selected' : ''}`}
                  onClick={() => !saving && handleThemeSelect(theme.code)}
                >
                  <div className="theme-preview" style={{
                    background: `linear-gradient(135deg, ${theme.preview.primaryColor} 0%, ${theme.preview.secondaryColor} 100%)`
                  }}>
                    <div className="theme-preview-colors">
                      <div className="color-dot" style={{ backgroundColor: theme.preview.primaryColor }}></div>
                      <div className="color-dot" style={{ backgroundColor: theme.preview.secondaryColor }}></div>
                      <div className="color-dot" style={{ backgroundColor: theme.preview.accentColor }}></div>
                    </div>
                  </div>
                  
                  <div className="theme-info">
                    <div className="theme-header">
                      <h4>{theme.name}</h4>
                      {selectedTheme === theme.code && (
                        <FaCheck className="selected-icon" />
                      )}
                    </div>
                    <p className="theme-description">{theme.description}</p>
                    
                    <div className="theme-meta">
                      <span className={`plan-badge ${theme.requiredPlan || 'free'}`}>
                        {theme.requiredPlan || 'free'}
                      </span>
                      <span className="style-badge">{theme.config.style}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          );
        })
      )}
    </div>
  );
};

export default TenantThemeSelector;

